import { useCallback } from 'react';
import { armDomainRedirect } from '@/lib/domainRedirectArm';
import { useNavigate, useLocation } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useUserContext } from '@/contexts/UserContext';
import { useLocalizedLocations, LocalizedLocation } from '@/hooks/useLocalizedLocations';
import { fetchTranslatedSlug } from '@/hooks/useTranslatedSlug';
import { fetchSlugResolution } from '@/hooks/useSlugResolver';
import { useAnalyticsTracking } from '@/hooks/useAnalyticsTracking';
import { INTERNATIONAL_LOCATION_ID } from '@/constants/config';
import { ROUTES } from '@/constants';
import { routeTo } from '@/lib/routeHelpers';
import { useLocale } from '@/contexts/LocaleContext';
import { DOMAIN_DEFAULTS } from '@/lib/domainDefaults';
import { BRAZIL_LOCATION_ID } from '@/constants/branding';
import { useRouteEntitySafe } from '@/contexts/RouteEntityContext';
interface LocationNavigationOptions {
  /** Skip navigation (just update context) */
  skipNavigation?: boolean;
  /** Skip analytics tracking */
  skipAnalytics?: boolean;
  /** Additional metadata for analytics */
  metadata?: Record<string, unknown>;
}

interface UseLocationNavigationReturn {
  /** Select a location by location_id (handles analytics + context + navigation) */
  selectLocation: (locationId: string, options?: LocationNavigationOptions) => void;
  /** Select International/Any Location (fallback for "location not detected") */
  selectInternational: (options?: LocationNavigationOptions) => void;
  /** Current location_id from context */
  currentLocation: string;
  /** Loading state (context + locations) */
  isLoading: boolean;
  /** Locations data */
  locations: LocalizedLocation[];
}

/**
 * Unified hook for location selection across all components.
 * Encapsulates analytics tracking, context updates, and navigation.
 * 
 * NOTE: All location identifiers are now location_id (not iso_alpha2)
 * 
 * @example
 * const { selectLocation, selectInternational, isLoading } = useLocationNavigation();
 * 
 * // Select a specific location by location_id (navigates to localized URL)
 * selectLocation('123'); // location_id
 * 
 * // Select international (navigates to home)
 * selectInternational();
 * 
 * // Skip navigation (e.g., on Experts page)
 * selectLocation('123', { skipNavigation: true });
 */
export function useLocationNavigation(): UseLocationNavigationReturn {
  const navigate = useNavigate();
  const routeLocation = useLocation();
  const queryClient = useQueryClient();
  const { currentLocation, updateLocation, isLoading: contextLoading } = useUserContext();
  const { locations, isLoading: locationsLoading } = useLocalizedLocations();
  const { settings } = useLocale();
  const { trackLocationDetection, trackLocationSelect } = useAnalyticsTracking();

  const isLoading = contextLoading || locationsLoading;

  // Only the home page and location pages ARE a location, so only there does a
  // location switch move the visitor. Every other page stays where it is.
  const routeEntity = useRouteEntitySafe();
  const entityType = routeEntity?.currentEntity?.type;
  const isLocationScopedPage = routeLocation.pathname === ROUTES.ROOT || entityType === 'home' || entityType === 'location';

  const selectInternational = useCallback((options?: LocationNavigationOptions) => {
    const { skipNavigation = false, skipAnalytics = false, metadata } = options || {};

    // 1. Fire-and-forget analytics (non-blocking)
    if (!skipAnalytics) {
      trackLocationDetection(INTERNATIONAL_LOCATION_ID, 'user_selection', { 
        previousLocation: currentLocation,
        isExplicitUserChoice: true,
        ...metadata 
      });
      trackLocationSelect(INTERNATIONAL_LOCATION_ID);
    }

    // 2. Update context - explicit user selection
    armDomainRedirect();
    updateLocation(INTERNATIONAL_LOCATION_ID, 'user_selection');

    // 3. Navigate (unless skipped or already there)
    if (!skipNavigation && isLocationScopedPage && routeLocation.pathname !== ROUTES.ROOT) {
      navigate(ROUTES.ROOT, { replace: true, preventScrollReset: true, state: { preserveScroll: true } });
    }
  }, [currentLocation, updateLocation, navigate, routeLocation.pathname, isLocationScopedPage, trackLocationDetection, trackLocationSelect]);

  const selectLocation = useCallback(async (locationId: string, options?: LocationNavigationOptions) => {
    // Handle INTERNATIONAL_LOCATION_ID specially
    if (locationId === INTERNATIONAL_LOCATION_ID || locationId === 'GLOBAL') {
      selectInternational(options);
      return;
    }

    const { skipNavigation = false, skipAnalytics = false, metadata } = options || {};

    // 1. Fire-and-forget analytics (non-blocking)
    if (!skipAnalytics) {
      trackLocationDetection(locationId, 'user_selection', { 
        previousLocation: currentLocation,
        isExplicitUserChoice: true,
        ...metadata 
      });
      trackLocationSelect(locationId);
    }

    // 2. ALWAYS persist user selection immediately (before navigation)
    armDomainRedirect();
    updateLocation(locationId, 'user_selection');

    // 3. Navigation flow (home and location pages only)
    if (!skipNavigation && isLocationScopedPage) {
      // Special case: tributario.com.br + Brazil → navigate to root (not /brasil which gets normalized)
      if (DOMAIN_DEFAULTS.isTributario && locationId === BRAZIL_LOCATION_ID) {
        if (routeLocation.pathname !== '/') {
          navigate('/', { replace: true, preventScrollReset: true, state: { preserveScroll: true } });
        }
        return;
      }

      const loc = locations.find(l => l.id?.toString() === locationId);
      
      // Fetch translated slug for current locale
      const localizedSlug = loc?.id 
        ? await fetchTranslatedSlug('location', String(loc.id), settings.locale)
        : null;
      
      const targetSlug = localizedSlug || loc?.slug;
      
      if (targetSlug) {
        // PREFETCH: Warm the slug-resolution cache BEFORE navigating
        await queryClient.prefetchQuery({
          queryKey: ['slug-resolution', targetSlug],
          queryFn: () => fetchSlugResolution(targetSlug),
          staleTime: 5 * 60 * 1000,
        });
        
        const targetPath = routeTo.locationPage(targetSlug);
        if (routeLocation.pathname !== targetPath && !routeLocation.pathname.startsWith(targetPath + '/')) {
          navigate(targetPath, { replace: true, preventScrollReset: true, state: { preserveScroll: true } });
        }
      }
    }
  }, [currentLocation, locations, settings.locale, queryClient, updateLocation, navigate, routeLocation.pathname, isLocationScopedPage, selectInternational, trackLocationDetection, trackLocationSelect]);

  return {
    selectLocation,
    selectInternational,
    currentLocation,
    isLoading,
    locations,
  };
}
